import { useQuery } from "@tanstack/react-query";
import { useParams } from "wouter";
import { type Product } from "@/lib/schema";
import ContactForm from "@/components/ContactForm";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { useState, useEffect } from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import ProductPriceDisplay from "@/components/ProductPriceDisplay";

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: product, isLoading } = useQuery<Product>({
    queryKey: [`/api/products/${id}`],
  });
  const [selectedImage, setSelectedImage] = useState(0);

  // Reset selected image when product changes
  useEffect(() => {
    setSelectedImage(0);
  }, [product?.id]);

  if (isLoading) return <LoadingSpinner />;

  if (!product) return (
    <div className="container mx-auto px-4 py-8">
      <Card>
        <CardContent className="py-8">
          <h2 className="text-xl font-semibold text-center">Product not found</h2>
        </CardContent>
      </Card>
    </div>
  );

  const images = product.images || [];
  const currentImage = images[selectedImage] || images[0] || 'https://placehold.co/600x400/png';

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="aspect-square relative rounded-lg overflow-hidden">
            <img
              src={currentImage}
              alt={`${product.name} - Image ${selectedImage + 1}`}
              className="w-full h-full object-cover"
            />
          </div>

          {images.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`aspect-square rounded-md overflow-hidden border-2 ${
                    selectedImage === index ? 'border-primary' : 'border-transparent'
                  }`}
                >
                  <img src={image} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-4">{product.name}</h1>

            <div className="mb-6">
              <ProductPriceDisplay
                regularPrice={Number(product.price) || 0}
                product={{
                  name: product.name,
                  id: product.id.toString()
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <h3 className="font-semibold">Brand</h3>
                <p>{product.brand}</p>
              </div>
              <div>
                <h3 className="font-semibold">Reference</h3>
                <p>{product.reference}</p>
              </div>
              <div>
                <h3 className="font-semibold">Category</h3>
                <p>{product.category}</p>
              </div>
              <div>
                <h3 className="font-semibold">Sector</h3>
                <p>{product.sector}</p>
              </div>
              {product.condition === 'used' && product.year && (
                <div>
                  <h3 className="font-semibold">Year</h3>
                  <p>{product.year}</p>
                </div>
              )}
              {product.location && (
                <div>
                  <h3 className="font-semibold">Location</h3>
                  <p>{product.location}</p>
                </div>
              )}
            </div>

            <Separator className="my-6" />

            <div className="mb-6">
              <h3 className="font-semibold mb-4">Technical Specifications</h3>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(product.specs || {}).map(([key, value]) => (
                  <div key={key}>
                    <h4 className="font-medium text-sm text-muted-foreground">{key}</h4>
                    <p>{value}</p>
                  </div>
                ))}
              </div>
            </div>

            <Separator className="my-6" />

            <div>
              <h3 className="font-semibold mb-4">Description</h3>
              <p className="text-muted-foreground whitespace-pre-wrap">{product.description}</p>
            </div>
          </div>

          <div className="space-y-4">
            <ContactForm productId={product.id} />
          </div>
        </div>
      </div>
    </div>
  );
}
